import React, { useState, useEffect, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { expertApi } from '../../services/api';
import type { Expert, ExpertCreate, ExpertListItem } from '../../types';
import { ExpertEditForm } from './ExpertEditForm';
import { rankTemplatesBySimilarity } from '../../utils/templateSearch';

const TEMPLATE_PAGE_SIZE = 12;

interface ExpertFormProps {
  open: boolean;
  onClose: () => void;
  expert?: Expert;
  onSuccess?: (expert?: Expert) => void;
  // If provided, form will hide AI System selector and use this id when creating
  aiSystemId?: number | null;
}

export const ExpertForm: React.FC<ExpertFormProps> = ({ open, onClose, expert, onSuccess, aiSystemId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const isEditing = !!expert;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [formData, setFormData] = useState<ExpertCreate>({
    name: '',
    description: '',
    tags: [],
    system_prompt: '',
    prompt: '',
    model: 0,
    ai_system: null,
    input_data: {},
    for_loop: {},
    output: {},
    use_cache: true,
    cache_ttl_seconds: 3600,
    cache_max_uses: null,
    retry: 3,
    expert_type: 'ANALYSIS',
    dry_run: false,
    is_active: true,
    is_template: false,
    is_public: false,
    // Evaluator defaults
    use_evaluator: false,
    evaluator_model: null,
    evaluator_system_prompt: '',
    evaluator_accuracy_threshold: 70,
    evaluator_hallucination_threshold: 30,
    evaluator_on_failure: 'continue',
    evaluator_on_low_accuracy: 'stop',
    evaluator_on_high_hallucination: 'stop',
    evaluator_max_retries: 2,
    // Execution conditions
    execution_conditions: {},
  });

  const [error, setError] = useState<string | null>(null);
  const [templateExpertId, setTemplateExpertId] = useState<number | ''>('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateTagFilters, setTemplateTagFilters] = useState<string[]>([]);
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);
  const [selectedTemplateSnapshot, setSelectedTemplateSnapshot] = useState<ExpertListItem | null>(null);

  const templateExpertsQuery = useInfiniteQuery({
    queryKey: [
      'expert-templates',
      aiSystemId ?? 'none',
      templateSearch.trim(),
      templateTagFilters.join(','),
    ],
    queryFn: ({ pageParam }) =>
      expertApi.templates({
        page: pageParam,
        page_size: TEMPLATE_PAGE_SIZE,
        search: templateSearch.trim() || undefined,
        tags: templateTagFilters.length ? templateTagFilters.join(',') : undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
    enabled: open && !isEditing && typeof aiSystemId !== 'undefined' && aiSystemId !== null && templateGalleryOpen,
    staleTime: 60_000,
  });

  const templateResults = useMemo(
    () => templateExpertsQuery.data?.pages.flatMap((page) => page.results) || [],
    [templateExpertsQuery.data?.pages],
  );

  const selectedTemplate = useMemo(() => {
    return templateResults.find((template) => template.id === templateExpertId) || selectedTemplateSnapshot;
  }, [selectedTemplateSnapshot, templateExpertId, templateResults]);

  const rankedTemplates = useMemo(
    () => rankTemplatesBySimilarity(templateResults, templateSearch),
    [templateResults, templateSearch],
  );

  const templateTagOptions = useMemo(() => {
    const counts = new Map<string, number>();
    templateResults.forEach((template) => {
      (template.tags || []).forEach((tag) => {
        counts.set(tag, (counts.get(tag) || 0) + 1);
      });
    });

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag)
      .slice(0, 12);
  }, [templateResults]);

  const visibleTemplates = useMemo(() => {
    const baseTemplates = templateSearch.trim()
      ? rankedTemplates
      : templateResults;

    if (!templateTagFilters.length) {
      return baseTemplates;
    }

    return baseTemplates.filter((template) => {
      const templateTags = new Set((template.tags || []).map((tag) => tag.toLowerCase()));
      return templateTagFilters.every((tag) => templateTags.has(tag.toLowerCase()));
    });
  }, [rankedTemplates, templateResults, templateSearch, templateTagFilters]);

  useEffect(() => {
    if (expert) {
      console.log('🔍 ExpertForm useEffect - expert prop:', expert);
      console.log('🔍 ExpertForm useEffect - expert.use_evaluator:', expert.use_evaluator);
      setFormData({
        name: expert.name,
        description: expert.description || '',
        tags: expert.tags || [],
        system_prompt: expert.system_prompt,
        prompt: expert.prompt,
        model: expert.model,
        ai_system: expert.ai_system,
        input_data: expert.input_data,
        for_loop: expert.for_loop ?? {},
        output: expert.output,
        use_cache: expert.use_cache,
        cache_ttl_seconds: expert.cache_ttl_seconds ?? 3600,
        cache_max_uses: expert.cache_max_uses ?? null,
        retry: expert.retry,
        expert_type: expert.expert_type,
        dry_run: expert.dry_run,
        is_active: expert.is_active,
        is_template: expert.is_template ?? false,
        is_public: expert.is_public ?? false,
        code: expert.code, // Load code field for CODE experts
        // Evaluator fields
        use_evaluator: expert.use_evaluator ?? false,
        evaluator_model: expert.evaluator_model ?? null,
        evaluator_system_prompt: expert.evaluator_system_prompt ?? '',
        evaluator_accuracy_threshold: expert.evaluator_accuracy_threshold ?? 70,
        evaluator_hallucination_threshold: expert.evaluator_hallucination_threshold ?? 30,
        evaluator_on_failure: expert.evaluator_on_failure ?? 'continue',
        evaluator_on_low_accuracy: expert.evaluator_on_low_accuracy ?? 'stop',
        evaluator_on_high_hallucination: expert.evaluator_on_high_hallucination ?? 'stop',
        evaluator_max_retries: expert.evaluator_max_retries ?? 2,
        // Execution conditions
        execution_conditions: expert.execution_conditions ?? {},
      });
    } else {
      setFormData({
        name: '',
        description: '',
        tags: [],
        system_prompt: '',
        prompt: '',
        model: 0,
        ai_system: null,
        input_data: { inputs: [] },
        for_loop: {},
        output: {},
        use_cache: true,
        cache_ttl_seconds: 3600,
        cache_max_uses: null,
        retry: 3,
        expert_type: 'ANALYSIS',
        dry_run: false,
        is_active: true,
        is_template: false,
        is_public: false,
        // Evaluator defaults
        use_evaluator: false,
        evaluator_model: null,
        evaluator_system_prompt: '',
        evaluator_accuracy_threshold: 70,
        evaluator_hallucination_threshold: 30,
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
        evaluator_max_retries: 2,
        // Execution conditions
        execution_conditions: {},
      });
    }
    setError(null);
    setTemplateExpertId('');
    setTemplateSearch('');
    setTemplateTagFilters([]);
    setTemplateGalleryOpen(false);
    setSelectedTemplateSnapshot(null);
  }, [expert, open]);

  // If aiSystemId prop is provided and we are creating a new expert, set it
  useEffect(() => {
    if (!expert && typeof aiSystemId !== 'undefined') {
      setFormData((prev) => ({ ...prev, ai_system: aiSystemId ?? null }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expert, open, aiSystemId]);

  const createMutation = useMutation({
    mutationFn: (data: ExpertCreate) => {
      console.log('📤 CREATE MUTATION - Sending data:', data);
      console.log('📤 input_data being sent:', JSON.stringify(data.input_data, null, 2));
      // If aiSystemId prop provided, pass it as query param so backend
      // assigns the expert to that AI System and the UI can hide the field.
      if (typeof aiSystemId !== 'undefined' && aiSystemId !== null) {
        return expertApi.create(data, { ai_system: aiSystemId });
      }
      return expertApi.create(data);
    },
    onSuccess: (responseData) => {
      console.log('✅ CREATE SUCCESS - Response:', responseData);
      console.log('✅ Saved input_data:', JSON.stringify(responseData.input_data, null, 2));
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      onClose();
      onSuccess?.(responseData);
    },
    onError: (err: any) => {
      console.error('❌ CREATE ERROR:', err);
      setError(err.response?.data?.detail || 'Failed to create expert');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ExpertCreate) => {
      console.log('📤 UPDATE MUTATION - Sending data:', data);
      console.log('📤 input_data being sent:', JSON.stringify(data.input_data, null, 2));
      return expertApi.update(expert!.id, data);
    },
    onSuccess: (responseData) => {
      console.log('✅ UPDATE SUCCESS - Response:', responseData);
      console.log('✅ Saved input_data:', JSON.stringify(responseData.input_data, null, 2));
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      onClose();
      onSuccess?.(responseData);
    },
    onError: (err: any) => {
      console.error('❌ UPDATE ERROR:', err);
      setError(err.response?.data?.detail || 'Failed to update expert');
    },
  });

  const copyTemplateMutation = useMutation({
    mutationFn: (templateId: number) =>
      expertApi.clone(templateId, {
        ai_system: aiSystemId ?? null,
        name: formData.name?.trim() || undefined,
      }),
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      (responseData.warnings || []).forEach((warning) => toast.info(warning));
      onClose();
      onSuccess?.(responseData.expert);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to copy expert template');
    },
  });

  const handleCopyFromTemplate = () => {
    if (!templateExpertId) {
      setError('Select an expert template');
      return;
    }
    copyTemplateMutation.mutate(Number(templateExpertId));
  };

  const handleTemplateChange = (templateId: number | '') => {
    setTemplateExpertId(templateId);
    if (!templateId) {
      setSelectedTemplateSnapshot(null);
      setTemplateSearch('');
      return;
    }

    const template = templateResults.find((item) => item.id === templateId);
    if (!template) return;

    setSelectedTemplateSnapshot(template);
    setTemplateSearch(template.name);
    setFormData((prev) => ({
      ...prev,
      name: prev.name?.trim() ? prev.name : `${template.name} Copy`,
    }));
  };

  const toggleTemplateTagFilter = (tag: string) => {
    setTemplateTagFilters((current) =>
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag]
    );
  };

  const handleTemplateGalleryScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (
      distanceToBottom < 96 &&
      templateExpertsQuery.hasNextPage &&
      !templateExpertsQuery.isFetchingNextPage
    ) {
      templateExpertsQuery.fetchNextPage();
    }
  };

  const handleSubmit = () => {
    // For CODE experts, prompt is not required (they use code field instead)
    if (!formData.name) {
      setError('Please fill in the expert name');
      return;
    }
    
    // Non-CODE experts require a prompt
    if (formData.expert_type !== 'CODE' && !formData.prompt) {
      setError('Please fill in the prompt');
      return;
    }

    // For AGENT type, require agent selection instead of model
    if (formData.expert_type === 'AGENT') {
      if (!formData.agent) {
        setError('Please select an agent for AGENT type expert');
        return;
      }
    } else if (formData.expert_type === 'CODE') {
      // CODE type doesn't need a model - it executes Python code directly
      if (!formData.code || !formData.code.trim()) {
        setError('Please provide Python code for CODE type expert');
        return;
      }
    } else {
      // For ANALYSIS/ACTION types, model is required
      if (!formData.model) {
        setError('Please select a model');
        return;
      }
    }

    const forLoop = (formData.for_loop || {}) as Record<string, any>;
    if (Object.keys(forLoop).length > 0) {
      if (!forLoop.source || typeof forLoop.source !== 'string') {
        setError('For loop source is required when loop execution is enabled');
        return;
      }
      if (!forLoop.item_alias || typeof forLoop.item_alias !== 'string') {
        setError('For loop item alias is required when loop execution is enabled');
        return;
      }
      if (forLoop.max_items != null && Number(forLoop.max_items) < 1) {
        setError('For loop max items must be at least 1');
        return;
      }
      if (forLoop.concurrency != null && Number(forLoop.concurrency) < 1) {
        setError('For loop concurrency must be at least 1');
        return;
      }
    }

    // Prepare data for submission - ensure model is null for AGENT and CODE types
    const submitData = {
      ...formData,
      for_loop: Object.keys(forLoop).length > 0 ? forLoop : {},
      model: (formData.expert_type === 'AGENT' || formData.expert_type === 'CODE') ? null : formData.model,
      // For CODE experts, set prompt to code if prompt is empty (backend requires non-blank prompt)
      prompt: formData.expert_type === 'CODE' && !formData.prompt?.trim() 
        ? (formData.code || 'CODE_EXPERT') 
        : formData.prompt,
      // When evaluator is disabled, reset action fields to valid defaults
      ...(formData.use_evaluator ? {} : {
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
      } as const),
      // When CODE type, force evaluator off
      ...(formData.expert_type === 'CODE' ? {
        use_evaluator: false,
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
      } as const : {}),
    };

    // DEBUG: Log the complete form data being submitted
    console.group('🔍 EXPERT FORM SUBMIT DEBUG');
    console.log('Full formData:', formData);
    console.log('submitData (with model fix):', submitData);
    console.log('input_data structure:', JSON.stringify(formData.input_data, null, 2));
    console.log('Number of inputs:', formData.input_data?.inputs?.length || 0);
    console.groupEnd();

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || copyTemplateMutation.isPending;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "lg"} 
      fullWidth 
      fullScreen={isMobile || isFullscreen}
      PaperProps={{
        sx: {
          ...((isFullscreen || isMobile) ? {} : { minHeight: '80vh', maxHeight: '90vh' })
        }
      }}
    >
      <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            {isEditing ? 'Edit Expert' : 'Create Expert'}
          </Typography>
          {!isMobile && (
            <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          {!isEditing && typeof aiSystemId !== 'undefined' && aiSystemId !== null && (
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Copy from template
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => setTemplateGalleryOpen((value) => !value)}
                    endIcon={templateGalleryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    {templateGalleryOpen ? 'Hide templates' : 'Browse templates'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCopyFromTemplate}
                    disabled={!templateExpertId || isLoading}
                    sx={{ flexShrink: 0 }}
                  >
                    Copy Template
                  </Button>
                </Stack>
              </Stack>

              <Paper variant="outlined" sx={{ px: 1.25, py: 1, bgcolor: 'action.hover' }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box minWidth={0}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedTemplate ? selectedTemplate.name : 'No template selected'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {selectedTemplate?.description
                        || selectedTemplate?.system_name
                        || 'Create this expert from a blank form.'}
                    </Typography>
                  </Box>
                  {selectedTemplate && (
                    <Button size="small" onClick={() => handleTemplateChange('')}>
                      Clear
                    </Button>
                  )}
                </Stack>
              </Paper>

              <Collapse in={templateGalleryOpen} unmountOnExit>
                <Stack spacing={1.25}>
                  <TextField
                    value={templateSearch}
                    onChange={(event) => setTemplateSearch(event.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Search expert templates by tag, description, type, or system"
                    disabled={isLoading || templateExpertsQuery.isLoading}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  {!!templateTagOptions.length && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                      {templateTagOptions.map((tag) => {
                        const selected = templateTagFilters.includes(tag);
                        return (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            clickable
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => toggleTemplateTagFilter(tag)}
                          />
                        );
                      })}
                    </Stack>
                  )}

                  {templateExpertsQuery.isError && (
                    <Typography variant="caption" color="error">
                      Expert templates could not be loaded.
                    </Typography>
                  )}

                  {templateExpertsQuery.isLoading ? (
                    <Box sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Loading expert templates
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      onScroll={handleTemplateGalleryScroll}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1,
                        maxHeight: 280,
                        overflowY: 'auto',
                        pr: 0.5,
                      }}
                    >
                      {visibleTemplates.map((template) => {
                        const selected = template.id === templateExpertId;
                        return (
                          <Paper
                            key={template.id}
                            component="button"
                            type="button"
                            variant="outlined"
                            onClick={() => handleTemplateChange(template.id)}
                            sx={{
                              p: 1.5,
                              minHeight: 122,
                              textAlign: 'left',
                              cursor: 'pointer',
                              borderColor: selected ? 'primary.main' : 'divider',
                              bgcolor: selected ? 'action.selected' : 'background.paper',
                              color: 'text.primary',
                              '&:hover': {
                                borderColor: selected ? 'primary.main' : 'text.secondary',
                                bgcolor: selected ? 'action.selected' : 'action.hover',
                              },
                            }}
                          >
                            <Stack spacing={0.75} sx={{ height: '100%' }}>
                              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                                <Box minWidth={0}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                    {template.name}
                                  </Typography>
                                  {template.system_name && (
                                    <Typography variant="caption" color="text.secondary">
                                      {template.system_name}
                                    </Typography>
                                  )}
                                </Box>
                                {selected && <CheckCircleIcon color="primary" fontSize="small" />}
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {template.description || template.expert_type_display || 'No description added.'}
                              </Typography>
                              {!!template.tags?.length && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 'auto', rowGap: 0.5 }}>
                                  {template.is_public && (
                                    <Chip label="Public" size="small" color="info" variant="outlined" sx={{ height: 20 }} />
                                  )}
                                  {template.tags.slice(0, 5).map((tag) => (
                                    <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 20 }} />
                                  ))}
                                </Stack>
                              )}
                              {!template.tags?.length && template.is_public && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 'auto', rowGap: 0.5 }}>
                                  <Chip label="Public" size="small" color="info" variant="outlined" sx={{ height: 20 }} />
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}

                  {!templateExpertsQuery.isLoading && visibleTemplates.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No expert templates match the current search or tag filters.
                    </Typography>
                  )}

                  {templateExpertsQuery.isFetchingNextPage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">
                        Loading more expert templates
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Collapse>
            </Stack>
          )}
          <ExpertEditForm
            expert={expert}
            formData={formData}
            onChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
            disabled={isLoading}
            hideSystemSelector={typeof aiSystemId !== 'undefined'}
            promptMinHeight={isFullscreen ? 600 : isMobile ? 350 : 450}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '& > button': { width: isMobile ? '100%' : 'auto' }
      }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
